import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { catchError, forkJoin, of } from 'rxjs';
import { environment } from '../../../environments/environment';

export type Language = 'fr' | 'en';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  readonly language = signal<Language>('fr');
  readonly dictionary = signal<Record<string, string>>({});

  constructor() {
    const saved = isPlatformBrowser(this.platformId) ? localStorage.getItem('agbe-language') : null;
    this.setLanguage(saved === 'en' ? 'en' : 'fr');
  }

  setLanguage(language: Language): void {
    this.language.set(language);
    this.document.documentElement.lang = language;
    if (isPlatformBrowser(this.platformId)) localStorage.setItem('agbe-language', language);
    const local$ = this.http.get<Record<string, string>>(`/i18n/${language}.json`)
      .pipe(catchError(() => of<Record<string, string>>({})));
    const overrides$ = this.http.get<Record<string, string>>(`${environment.apiUrl}/translations`, { params: { lang: language } })
      .pipe(catchError(() => of<Record<string, string>>({})));
    forkJoin([local$, overrides$]).subscribe(([local, overrides]) => {
      this.dictionary.set({ ...local, ...overrides });
    });
  }

  t(key: string, fallback = key): string { return this.dictionary()[key] ?? fallback; }
  localized<T extends Record<string, unknown>>(item: T, field: string): string {
    return String(item[`${field}_${this.language()}`] ?? item[`${field}_fr`] ?? '');
  }
}
